import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BeatLoader } from "react-spinners";

import styles from "./RegistrationUserModule.module.scss";
import Input from "../../ui/Input/Input";
import { addEmployee, updateEmployee } from "../../utils/api/actions/user";

// Иконки для компонентов
const EyeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 4.37C3.75 4.37 1.25 10 1.25 10C1.25 10 3.75 15.63 10 15.63C16.25 15.63 18.75 10 18.75 10C18.75 10 16.25 4.37 10 4.37Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 13.12C11.7259 13.12 13.12 11.7259 13.12 10C13.12 8.27412 11.7259 6.88 10 6.88C8.27412 6.88 6.88 8.27412 6.88 10C6.88 11.7259 8.27412 13.12 10 13.12Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16.58 16.58L3.42 3.42"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.38 7.03C7.8319 7.57906 7.5294 8.32001 7.53 9.09C7.53 10.7 8.83 12 10.44 12C11.21 12 11.93 11.71 12.48 11.19"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.1199 13.7C13.0799 14.45 11.8299 14.89 10.4399 14.89C5.95988 14.89 3.16988 9.09 3.16988 9.09C4.01988 7.44 5.03988 6.09 6.11988 5.11"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.4399 3.29C16.2699 3.29 19.0599 9.09 19.0599 9.09C18.7199 9.82 18.2999 10.51 17.8199 11.16"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const KeyIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 13L15 7M10.5 8.5L7.5 7L7 10L9 11.5L10.5 8.5ZM17 15L13.5 13.5L14 11L17 15ZM12.5 12.5L14 14L12.5 12.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 11.5C9 11.5 7.5 13 6 13C4.5 13 3 12 3 10C3 8 4.5 7 6 7C7.5 7 9 8.5 9 8.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15 17.5C15 17.5 16.5 16 18 16C19.5 16 21 17 21 19C21 21 19.5 22 18 22C16.5 22 15 20.5 15 20.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const RegistrationUserModule = ({ setIsUserAdd }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user?.data);
  const { isUpdateEmployees, editEmployeeData } = useSelector(
    (state) => state?.employees
  );

  // Предзаполнение данных при редактировании сотрудника
  const initialFormData = {
    fullName: isUpdateEmployees
      ? `${editEmployeeData?.surname || ""} ${
          editEmployeeData?.firstName || ""
        } ${editEmployeeData?.patronymic || ""}`.trim()
      : "",
    email: isUpdateEmployees ? editEmployeeData?.email || "" : "",
    phone: isUpdateEmployees ? editEmployeeData?.phone || "" : "",
    password: isUpdateEmployees ? "" : "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
  });
  const [formattedPhone, setFormattedPhone] = useState(
    isUpdateEmployees ? formatPhoneNumber(editEmployeeData?.phone || "") : ""
  );
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const phoneInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  // Функция для валидации формы
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Валидация ФИО
    if (!formData.fullName.trim()) {
      errors.fullName = "ФИО обязательно для заполнения";
      isValid = false;
    } else if (formData.fullName.trim().split(/\s+/).length < 2) {
      errors.fullName = "Введите фамилию и имя";
      isValid = false;
    }

    // Валидация email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = "Email обязателен для заполнения";
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Введите корректный email";
      isValid = false;
    }

    // Валидация телефона
    if (!formData.phone) {
      errors.phone = "Телефон обязателен для заполнения";
      isValid = false;
    } else if (formData.phone.length < 11) {
      errors.phone = "Телефон должен содержать 11 цифр";
      isValid = false;
    }

    // Валидация пароля только при создании нового сотрудника
    if (!isUpdateEmployees && !formData.password) {
      errors.password = "Пароль обязателен для заполнения";
      isValid = false;
    } else if (!isUpdateEmployees && formData.password.length < 8) {
      errors.password = "Пароль должен содержать минимум 8 символов";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Функция для форматирования телефона
  function formatPhoneNumber(value) {
    if (!value) return "";

    // Удаляем все нецифровые символы
    const phoneDigits = value.replace(/\D/g, "");

    // Ограничиваем длину до 11 цифр (для российских номеров)
    const digits = phoneDigits.substring(0, 11);

    // Форматируем для отображения
    let result = "";
    if (digits.length > 0) {
      result = "+";
      if (digits.length > 0) {
        result += digits.substring(0, 1);
      }
      if (digits.length > 1) {
        result += " (" + digits.substring(1, 4);
      }
      if (digits.length > 4) {
        result += ") " + digits.substring(4, 7);
      }
      if (digits.length > 7) {
        result += "-" + digits.substring(7, 9);
      }
      if (digits.length > 9) {
        result += "-" + digits.substring(9, 11);
      }
    }

    return result;
  }

  // Обработчик изменения телефона
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setFormattedPhone(formatted);

    // Сохраняем только цифры в стейте formData
    const phoneDigits = value.replace(/\D/g, "").substring(0, 11);
    setFormData({
      ...formData,
      phone: phoneDigits,
    });

    // Сбрасываем ошибку при изменении
    if (formErrors.phone) {
      setFormErrors({
        ...formErrors,
        phone: "",
      });
    }
  };

  // Обработчик для других полей
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Сбрасываем ошибку при изменении
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  // Генерация надежного пароля
  const generatePassword = () => {
    const lowerChars = "abcdefghijklmnopqrstuvwxyz";
    const upperChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const special = "!@#$%^&*";

    // Гарантируем, что пароль содержит как минимум по одному символу каждого типа
    let password = "";
    password += lowerChars.charAt(
      Math.floor(Math.random() * lowerChars.length)
    );
    password += upperChars.charAt(
      Math.floor(Math.random() * upperChars.length)
    );
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    password += special.charAt(Math.floor(Math.random() * special.length));

    // Добавляем остальные символы
    const allChars = lowerChars + upperChars + numbers + special;
    for (let i = 0; i < 8; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }

    // Перемешиваем символы
    password = password
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("");

    setFormData({
      ...formData,
      password,
    });

    // Сбрасываем ошибку пароля, если она была
    if (formErrors.password) {
      setFormErrors({
        ...formErrors,
        password: "",
      });
    }

    // Для эффекта выделения после генерации пароля
    if (passwordInputRef.current) {
      setTimeout(() => {
        passwordInputRef.current.focus();
        passwordInputRef.current.select();
      }, 50);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    console.log("object");

    // Валидация формы перед отправкой
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    if (isUpdateEmployees) {
      const { fullName, password, ...restFormData } = formData;

      // Разделение ФИО на компоненты
      const nameParts = fullName.trim().split(/\s+/);

      const parsedData = {
        id: editEmployeeData.id, // Используем ID редактируемого сотрудника
        ...restFormData,
        surname: nameParts[0] || "",
        firstName: nameParts[1] || "",
        patronymic: nameParts[2] || "",
        role: editEmployeeData.role,
      };

      // Добавляем пароль только если он был изменен
      if (password) {
        parsedData.password = password;
      }
      dispatch(updateEmployee(parsedData, handleRequestComplete));
    } else {
      const { fullName, ...restFormData } = formData;

      // Разделение ФИО на компоненты
      const nameParts = fullName.trim().split(/\s+/);

      const parsedData = {
        ...restFormData,
        surname: nameParts[0] || "",
        firstName: nameParts[1] || "",
        patronymic: nameParts[2] || "",
        companyId: user.companyId, // Добавляем ID компании
      };

      dispatch(
        addEmployee(parsedData, setLoading, handleRequestComplete, setIsUserAdd)
      );
    }
  };

  // Обработчик завершения запроса
  const handleRequestComplete = (success) => {
    setLoading(false);

    if (success) {
      // Сбрасываем форму после успешного добавления
      if (!isUpdateEmployees) {
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          password: "",
        });
        setFormattedPhone("");
      }
    }
  };

  // Сброс формы при смене режима (создание/редактирование)
  useEffect(() => {
    // Предзаполнение данных при редактировании
    if (isUpdateEmployees && editEmployeeData) {
      const fullName = `${editEmployeeData?.surname || ""} ${
        editEmployeeData?.firstName || ""
      } ${editEmployeeData?.patronymic || ""}`.trim();
      setFormData({
        fullName,
        email: editEmployeeData?.email || "",
        phone: editEmployeeData?.phone || "",
        password: "",
      });
      setFormattedPhone(formatPhoneNumber(editEmployeeData?.phone || ""));
    } else {
      // Сброс формы при создании нового сотрудника
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        password: "",
      });
      setFormattedPhone("");
    }
    setFormErrors({
      fullName: "",
      email: "",
      phone: "",
      password: "",
    });
  }, [isUpdateEmployees, editEmployeeData]);

  return (
    <form className={styles.registrationForm} onSubmit={handleSubmit}>
      <h2 className={styles.formTitle}>
        {isUpdateEmployees
          ? "Редактирование сотрудника"
          : "Регистрация нового сотрудника"}
      </h2>

      <div className={styles.inputGroup}>
        <Input
          label="ФИО"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          placeholder="Фамилия Имя Отчество"
          rightIcon={formData.fullName ? <CloseIcon size={16} /> : null}
          onClickRightIcon={() => {
            setFormData({ ...formData, fullName: "" });
            setFormErrors({ ...formErrors, fullName: "" });
          }}
          bottomString={
            formErrors.fullName || "Введите полное ФИО через пробел"
          }
          className={styles.inputWrapper}
          labelStyle={styles.inputLabel}
          inputStyle={`${styles.input} ${
            formErrors.fullName ? styles.inputError : ""
          }`}
          rightIconStyle={styles.rightIcon}
          backgroundStyle={`${styles.inputBackground} ${
            formErrors.fullName ? styles.backgroundError : ""
          }`}
        />
      </div>

      <div className={styles.inputGroup}>
        <Input
          label="Электронная почта"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="example@mail.ru"
          rightIcon={formData.email ? <CloseIcon size={16} /> : null}
          onClickRightIcon={() => {
            setFormData({ ...formData, email: "" });
            setFormErrors({ ...formErrors, email: "" });
          }}
          bottomString={formErrors.email || "Рабочая почта для связи"}
          className={styles.inputWrapper}
          labelStyle={styles.inputLabel}
          inputStyle={`${styles.input} ${
            formErrors.email ? styles.inputError : ""
          }`}
          rightIconStyle={styles.rightIcon}
          backgroundStyle={`${styles.inputBackground} ${
            formErrors.email ? styles.backgroundError : ""
          }`}
        />
      </div>

      <div className={styles.inputGroup}>
        <Input
          label="Телефон"
          name="phone"
          value={formattedPhone}
          onChange={handlePhoneChange}
          placeholder="+7 (___) ___-__-__"
          rightIcon={formData.phone ? <CloseIcon size={16} /> : null}
          onClickRightIcon={() => {
            setFormData({ ...formData, phone: "" });
            setFormattedPhone("");
            setFormErrors({ ...formErrors, phone: "" });
          }}
          refFocus={phoneInputRef}
          bottomString={formErrors.phone || ""}
          className={styles.inputWrapper}
          labelStyle={styles.inputLabel}
          inputStyle={`${styles.input} ${
            formErrors.phone ? styles.inputError : ""
          }`}
          rightIconStyle={styles.rightIcon}
          backgroundStyle={`${styles.inputBackground} ${
            formErrors.phone ? styles.backgroundError : ""
          }`}
        />
      </div>

      {!isUpdateEmployees && (
        <div className={styles.inputGroup}>
          <Input
            label={"Пароль"}
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleChange}
            placeholder={
              isUpdateEmployees ? "Введите новый пароль" : "Введите пароль"
            }
            rightIcon={showPassword ? <EyeIcon /> : <EyeOffIcon />}
            onClickRightIcon={() => setShowPassword(!showPassword)}
            refFocus={passwordInputRef}
            bottomString={formErrors.password || "Минимум 8 символов"}
            className={styles.inputWrapper}
            labelStyle={styles.inputLabel}
            inputStyle={`${styles.input} ${
              formErrors.password ? styles.inputError : ""
            }`}
            rightIconStyle={styles.rightIcon}
            backgroundStyle={`${styles.inputBackground} ${
              formErrors.password ? styles.backgroundError : ""
            }`}
          />

          <button
            type="button"
            className={styles.generatePasswordBtn}
            onClick={generatePassword}
          >
            <KeyIcon />
            <span>Сгенерировать пароль</span>
          </button>
        </div>
      )}

      <button type="submit" className={styles.submitButton} disabled={loading}>
        {loading ? (
          <>
            Подождите <BeatLoader color="#fff" size={8} margin={4} />
          </>
        ) : isUpdateEmployees ? (
          "Сохранить изменения"
        ) : (
          "Зарегистрировать"
        )}
      </button>
    </form>
  );
};
